<template>
  <v-container fill-height fluid v-if="contractsLoading">
    <v-row align="center" justify="center">
      <v-progress-circular
      indeterminate
      color="primary"
      />
    </v-row>
  </v-container>
  <v-card v-else :loading="templatesLoading" id="add-with-bundle-dialog" :class="containerClass">
    <v-card-title :class="contractColor">
      <span class="headline mr-4">{{title}}</span>
      <ActivityFormContractHeader :contract="contract" v-if="contract"/>
      <v-spacer />
      <BaseDialogActions hideRefresh />
    </v-card-title>
    <v-card-text>
      <v-form v-model="valid">
        <v-row class="mt-4">
          <v-col cols="12">
            <v-data-table
              id="add-with-bundle-table"
              item-key="activityBuilderId"
              v-if="activityBuilders"
              :items="activityBuilders"
              :loading.sync="templatesLoading"
              :headers="bundleHeaders"
              :expanded="expanded"
              :hide-default-footer="activityBuilders.length < 10"
            >
            <template #top>
              <v-container fluid>
                <v-row justify="end">
                  <Icon
                  :small="false"
                  icon="mdi-plus"
                  :tooltipText="$t('addDetail')"
                  @icon-clicked="addProductClicked"
                  elementId="add-detail"
                  />
                </v-row>
              </v-container>
            </template>
              <template #item.activityStatus="{ item }">
                <Icon
                dataTestId="activity-status-icon"
                :icon="item.activityStatus === 1 ? 'mdi-cancel' : 'mdi-checkbox-marked-circle'"
                :iconColor="item.activityStatus === 1 ? 'black' : 'success'"
                :tooltipColor="item.activityStatus === 1 ? 'black' : 'success'"
                :tooltipText="item.activityStatus === 1 ? $t('inactive') : $t('active')"
                :small="false"
                />
                <Icon
                v-if="item.specialization === TemplateSpecialization.Penalty.value"
                icon="weight-alert"
                :tooltipText="$t('penalty')"
                :small="false"
                />
              </template>
              <template #item.counterpartyName="{ item }">
                <v-col cols="12" v-if="builderIsDynamicCounterparty(item)" :style="`background-color: ${ runtimeFieldResolvesToDuplicateCounterparty(item.activityTemplateId, item.runtimeField) ? duplicateCounterpartyColorHue : 'unset'};`">
                  <v-row dense align="center">
                    <v-col cols="auto">
                      <Icon
                        icon="mdi-lock-open"
                        iconColor="black"
                        :tooltipText="$t('switchToConcreteCounterparty')"
                        :small="false"
                        @icon-clicked="item.runtimeField = undefined"
                        margin=""
                      />
                    </v-col>
                    <v-col cols="auto" class="mr-1">
                      <Icon
                        icon="mdi-alpha-r-circle-outline"
                        :iconColor="!rcpCanResolve(item.runtimeField) ? 'warning' : 'info'"
                        :small="false"
                        :tooltipText="counterpartyTextFromRuntimeField(item.runtimeField)"
                        :tooltipColor="!rcpCanResolve(item.runtimeField) ? 'warning' : 'info'"
                        margin=""
                      />
                    </v-col>
                    <v-col cols="">
                      {{ runtimeCounterparty(item.runtimeField) }}
                    </v-col>
                    <v-col cols="auto">
                      <Icon
                        v-if="runtimeFieldResolvesToDuplicateCounterparty(item.activityTemplateId, item.runtimeField)"
                        icon="mdi-alert"
                        :small="false"
                        :tooltipText="$t('duplicateActivityBuilder')"
                        margin=""
                      />
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="12" v-else-if="item.counterpartyName" :key="item.activityBuilderId">
                  {{ item.counterpartyName }}
                </v-col>
                <v-col cols="auto" class="pt-0 pb-0" v-else>
                  <v-autocomplete
                    :background-color="counterpartyIsDuplicate(item.activityTemplateId, item.counterparty?.accountId) ? duplicateCounterpartyColorHue : 'unset'"
                    v-model="item.counterparty"
                    item-text="name"
                    item-value="name"
                    return-object
                    :persistent-hint="counterpartyIsDuplicate(item.activityTemplateId, item.counterparty?.accountId)"
                    :hint="counterpartyIsDuplicate(item.activityTemplateId, item.counterparty?.accountId) ? $t('duplicateActivityBuilder') : ''"
                    :placeholder="$t('account')"
                    :rules="[rules.required]"
                    @input="duplicateCounterparties = getDuplicateCounterparties()"
                    :items="allAccounts"
                    class="w-100"
                    style="min-width: 12rem;"
                  >
                    <template #prepend v-if="!nullOrUndefined(item.runtimeFieldFromTemplate)">
                      <Icon
                        icon="mdi-arrow-u-left-top-bold"
                        :small="false"
                        iconColor="black"
                        :tooltipText="$t('switchToRuntimeCounterparty')"
                        @icon-clicked="{ item.runtimeField = item.runtimeFieldFromTemplate, delete item.counterparty }"
                      />
                    </template>
                  </v-autocomplete>
                </v-col>
              </template>
              <template #item.category="{ item }">
                {{ accountingCategoryFromInt(item.category) }}
              </template>
              <template #item.costBehavior="{ item }">
                {{ typeFromInt(item.costBehavior) }}
                <Icon
                v-if="item.productionType != null && item.productionType != ProductionCostType.Other.value"
                :icon="ProductionCostType.forInt(item.productionType).icon"
                iconColor="info"
                :tooltipText="ProductionCostType.forInt(item.productionType).name"/>
              </template>
              <template #item.glCode="{ item }">
                {{ item.glCode }}
              </template>
              <template #item.glOffset="{ item }">
                {{ !item.glOffset ? "N/A" : item.glOffset }}
              </template>
              <template #item.baseRate="{ item }">
                <span v-if="isPayByVolume(item)">
                  <Icon
                  v-if="item.rateSheet"
                  icon="mdi-note-text"
                  margin="mb-1 mr-1"
                  iconColor="success"
                  @icon-clicked="rateSheetDetailClicked(item)"/>
                  <span v-if="item.rateSheet?.name">
                    {{ getRateSheetName(item) }}
                  </span>
                  <Icon
                  v-if="!item.rateSheet"
                  icon="mdi-pencil"
                  @icon-clicked="rateSheetDetailClicked(item)"
                  :tooltipText="$t('defineRateSheet')"/>
                </span>
                <MoneyTextField
                v-else-if="allowBaseRate(item)"
                :key="item.activityBuilderId"
                :initialValue="item.baseRate"
                :itemId="item.activityBuilderId"
                :label="$t('rate')"
                :extraRules="['validActivityRate']"
                dense
                elementId="base-rate"
                @val-entered="baseRateEnteredForActivity"/>
                <span v-else>{{$t('notApplicable')}}</span>
              </template>
              <template #item.payOn="{ item }">
                {{ payOnFromInt(item.payOn)}}
              </template>
              <template #item.payBy="{ item }">
                {{ payByFromInt(item.payBy) }}
              </template>
              <template #item.modifier="{ item }">
                {{ modifierFromInt(item.modifier) }}
                <Icon
                icon="mdi-alert"
                iconColor="warning"
                v-if="showMissingMilesIcon(item)"
                :tooltipText="$t('contractMissingMilesToDestination')"
                class="lh-0"/>
              </template>
              <template v-slot:expanded-item="{ headers, item: builder }">
                <td :colspan="headers.length + 2" :key="builder.activityBuilderId" v-if="allowBaseRate(builder)" id="activity-expanded">
                  <v-container fluid>
                    <v-row align="center">
                      <div v-if="builder.products?.length === 0" style="width: 30px; height: 150px"/>
                      <v-col
                      v-for="product in builder.products"
                      :key="product.productId"
                      cols="12" md="4" lg="3" xl="2"
                      >
                        <v-card>
                          <v-card-title class="py-0 px-2">
                            <span style="font-size: 14px;" class="pa-1 ma-0">{{product.name}}</span>
                            <v-spacer></v-spacer>
                            <Icon
                              icon="mdi-close"
                              iconColor="grey"
                              @icon-clicked="removeProductDetail(builder, product)"
                              :tooltipText="$t('removeDetail')"
                              elementId="remove-detail"
                            />
                          </v-card-title>
                          <v-card-text>
                            <MoneyTextField
                            :initialValue="product.baseRate"
                            :itemId="`${builder.activityBuilderId}-${product.productId}`"
                            :minimumValueConfig="{ min: builder.baseRate, error: $t('baseRateLowerThanActivity') }"
                            :extraRules="['validActivityRate']"
                            :label="$t('rate')"
                            :expectedValue="expectedValue(builder.activityTemplateId, product.productId)"
                            dense
                            elementId="rate"
                            @blur-entered="baseRateEnteredForProduct"/>
                          </v-card-text>
                        </v-card>
                      </v-col>
                      <v-col cols="auto" v-if="products.length > 0">
                        <v-menu>
                          <template #activator="{ on: menu }">
                            <v-tooltip bottom color="grey">
                              <template #activator="{ on: tooltip }">
                                <v-icon
                                v-on="{ ...menu, ...tooltip }"
                                v-show="missingProducts(builder, products).length > 0"
                                height="24"
                                v-if="products.length > 0"
                                >
                                  mdi-plus
                                </v-icon>
                              </template>
                              <span class="subtitle-1 white--text">{{$t('addByproductDetail')}}</span>
                            </v-tooltip>
                          </template>
                          <v-list>
                            <v-list-item
                            v-for="product in missingProducts(builder, allProducts)"
                            :key="product.productId"
                            link
                            @click="addProductDetail(builder, product)">
                              {{product.name}}
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </v-col>
                    </v-row>
                  </v-container>
                </td>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <v-card-actions class="pb-4">
      <v-row no-gutters>
        <v-col>
          <v-btn id="cancel-bundle" text outlined color="black" @click="$emit('close')">{{$t('cancel')}}</v-btn>
        </v-col>
        <v-col cols="auto">
          <v-btn id="submit-bundle" :class="contractColor" :disabled="!valid" @click="saveBtnClicked">{{ $t('submit')}}</v-btn>
        </v-col>
      </v-row>
    </v-card-actions>
    <Dialog :stateId="dialogId" maxWidth="600">
      <v-card id="new-product-detail" :class="newDetailDialogClass">
        <v-card-title :class="contractColor">
          <v-row>
            <v-col>
              <span class="headline mr-4">{{$t(contractMode.value === ContractMode.Byproducts.value ? 'newByproductDetail' : 'newProductDetail')}}</span>
            </v-col>
            <v-col cols="auto">
              <BaseDialogActions hideRefresh hideFullscreen />
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text>
          <v-container fluid>
            <ProductSelector
            elementId="select-product"
            class="accommodate-button"
            :includeByproducts="contractMode.value === ContractMode.Byproducts.value"
            :includeLogsProducts="contractMode.value !== ContractMode.Byproducts.value"
            @products-selected="productsSelected"
            :excludeProductIds="products.map(p => p.productId)"
            />
          </v-container>
        </v-card-text>
        <v-card-actions class="mx-0 mb-2">
          <v-row no-gutters justify="end">
            <v-btn :color="contractColor" @click="addProductSubmitted" id="add-product">{{$t('add')}}</v-btn>
          </v-row>
        </v-card-actions>
      </v-card>
    </Dialog>
    <Dialog :stateId="rateSheetDialogId" @close="closeRateSheetDialog" :enforcePersistence="editingRateSheet">
      <RateSheetEditor
      v-if="focusedBuilder && contract"
      :accountId="contract.destinationAccountId"
      :existingRateSheet="focusedBuilder.rateSheet"
      :initDefinition="focusedBuilder.rateSheet?.rateSheetDefinition"
      :contract="contract"
      @confirm="rateSheetConfirmed"
      @cancel="closeRateSheetDialog"
      @editing-ratesheet="editingRateSheet = true"
      @close="closeRateSheetDialog"
      />
    </Dialog>
    <Dialog :stateId="uncoveredCombinationsDialogId" @close="uncoveredCombinations = null">
      <UncoveredLogCombinationsDialog
      v-if="uncoveredCombinations"
      :gap="uncoveredCombinations"/>
    </Dialog>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  PayOn, PayBy, AccountingCategory, ActivityModifier, CostType, ProductionCostType, ContractMode, TemplateSpecialization, RuntimeCounterparty
} from '@/utils/Enumerations'
import ActivityTemplateHeaders from '@/headers/Activity'
import fieldRules from '@/utils/rules.js'
import { colorClassForContractMode, contractModeFor } from '@/utils/componentHelpers.js'
import { formatMoney } from '@/utils/NumericMutations'
import { indexArrayBy } from '@/utils/base'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { expansionCoverageGapFromError } from '@/utils/ExpansionCoverage.js'
import { kebabCase } from 'lodash'
import { ActivitiesUpdateRequest, ActivityDetailCreateRequest } from '@/components/activity/Activity'

export default {
  name: 'AddWithBundleDialog',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    ActivityFormContractHeader: () => import('@/components/activity/ActivityFormContractHeader.vue'),
    MoneyTextField: () => import('@/components/helper/MoneyTextField'),
    Icon: () => import('@/components/helper/Icon.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    ProductSelector: () => import('@/components/tract/tract-form/ProductSelector.vue'),
    RateSheetEditor: () => import('@/components/activity/rate-sheet/RateSheetEditor.vue'),
    UncoveredLogCombinationsDialog: () => import('@/components/activity/rate-sheet/UncoveredLogCombinationsDialog.vue')
  },

  props: {
    contractId: { type: Number, required: true },
    bundle: { type: Object, required: true }
  },

  data: () => ({
    TemplateSpecialization,
    ContractMode,
    ProductionCostType,
    rules: fieldRules.rules,
    contract: undefined,
    activityBuilders: undefined,
    headers: [
      {
        sortable: true,
        text: 'Name',
        value: 'name',
        align: 'left'
      }
    ],
    valid: false,
    dialogId: uniqueDialogId('add-product-dialog'),
    rateSheetDialogId: uniqueDialogId('add-bundle-rate-sheet-dialog'),
    productSelectorState: undefined,
    products: [],
    runtimeFieldCounterpartyIdMap: new Map(),
    duplicateCounterparties: {},
    focusedBuilder: null,
    editingRateSheet: false,
    accountRateSheetDefinitionsFetched: false,
    uncoveredCombinations: null,
    uncoveredCombinationsDialogId: uniqueDialogId('add-bundle-uncovered-log-combinations')
  }),

  watch: {
    activityBuilders: {
      handler () {
        this.duplicateCounterparties = this.getDuplicateCounterparties()
      },
      deep: true
    }
  },

  computed: {
    ...mapGetters('activity-templates', ['templatesLoading', 'allTemplateBundles']),
    ...mapGetters('contract', ['contractsLoading']),
    ...mapGetters('account', ['allAccounts']),
    ...mapGetters('product', ['allProducts']),
    bundleHeaders () {
      const bundleHeaders = [...ActivityTemplateHeaders.templateBundleHeaders(this.$i18n.locale)]
      bundleHeaders.pop()
      return bundleHeaders
    },
    title () {
      switch (this.contractMode.value) {
        case 0: return this.$t('editingContract')
        case 1: return this.$t('editingByproductContract')
        case 3: return this.$t('editingLogYardSaleContract')
      }
      return this.isByProducts ? this.$t('editingByproductContract') : this.$t('editingContract')
    },

    contractColor () {
      return colorClassForContractMode(this.contractMode.value)
    },

    expanded () {
      return this.products.length > 0 ? this.activityBuilders.filter(ab => !ab.limitToBaseRate) : []
    },

    containerClass () {
      return kebabCase(this.contractMode.label).concat(' add-with-bundle-dialog')
    },

    newDetailDialogClass () {
      return kebabCase(this.contractMode.label).concat(' new-detail-dialog')
    },

    accountIndices () {
      return this.indexArrayBy(this.allAccounts, a => a.accountId)
    },

    contractMode () {
      return this.contract ? contractModeFor(this.contract.type) : ContractMode.Logs
    }
  },

  async created () {
    this.duplicateCounterpartyColorHue = '#ff000025'
    this.activityBuilders = [...this.bundle.activityBuilders.map(ab => ({
      ...ab,
      runtimeField: this.sanitizeRTCP(ab.runtimeField),
      runtimeFieldFromTemplate: this.sanitizeRTCP(ab.runtimeField),
      products: []
    }))]
    this.loading = true
    try {
      this.contract = await this.getContract(this.contractId)
      this.initializeCounterpartyRuntimeFieldMap()
      this.duplicateCounterparties = this.getDuplicateCounterparties()
    } finally {
      this.loading = false
    }
  },

  methods: {
    ...mapActions('contract', ['updateContract', 'getContract', 'updateContractProducts']),
    ...mapActions('activity', ['updateActivities']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('activity-detail', ['createActivityDetail']),
    ...mapActions('rate-sheet-definition', ['fetchAccountRateSheetDefinitions']),

    modifierFromInt: (x) => ActivityModifier.fromInt(x),
    typeFromInt: (x) => CostType.fromInt(x),
    payByFromInt: (val) => PayBy.fromInt(val),
    payOnFromInt: (val) => PayOn.fromInt(val),
    accountingCategoryFromInt: (val) => AccountingCategory.fromInt(val),
    runtimeCounterparty: (val) => RuntimeCounterparty.fromInt(val),
    nullOrUndefined: (val) => val === null || val === undefined,
    formatMoney,
    indexArrayBy,

    async saveBtnClicked () {
      if (!this.areBuildersValid()) return
      const flattenedProducts = this.activityBuilders.flatMap(ab => ab.products.map(({ productId, name }) => ({ productId, name })) ?? [])
      const contractProducts = Array.from(new Map(flattenedProducts.map(p => [p.productId, p])).values())
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((a, idx) => ({ ...a, sortOrder: idx }))

      await this.updateContractProducts({ contractId: this.contractId, products: contractProducts })

      for (const builder in this.activityBuilders) {
        const b = this.activityBuilders[builder]
        if (b.counterparty) {
          b.counterpartyId = b.counterparty.accountId
          b.runtimeCounterparty = undefined
        } else if (b.runtimeField) {
          b.runtimeCounterparty = b.runtimeField
          b.counterpartyId = undefined
        }
        b.contractId = this.contractId
        if (this.allowBaseRate(b)) {
          b.baseCost = { payBy: undefined, payOn: undefined, rate: undefined, rateSheet: undefined }
          b.baseCost.payBy = b.payBy
          b.baseCost.payOn = b.payOn
          b.baseCost.rate = b.baseRate
          if (b.payBy === PayBy.Volume.value && b.rateSheet) {
            b.baseCost.rateSheet = b.rateSheet
          }
        } else {
          b.baseCost = null
        }
        b.templateId = b.activityTemplateId
        b.contractorId = b.counterpartyId
        b.effectiveDate = this.contract.effectiveDate

        if (b.products?.length > 0) {
          for (const product of b.products) {
            const detailCreateRequest = new ActivityDetailCreateRequest({
              productId: product.productId,
              rate: product.baseRate
            })
            if (b.activityDetails == null) b.activityDetails = []
            b.activityDetails.push(detailCreateRequest)
          }
        }
      }

      const requestObj = new ActivitiesUpdateRequest({
        contractId: this.contractId,
        deletedActivityIds: [],
        updatedActivities: [],
        newActivities: this.activityBuilders
      })

      try {
        await this.updateActivities(requestObj)
      } catch (error) {
        this.showUncoveredCombinations(error)
        return
      }

      this.$emit('added-activities-close')
    },

    showUncoveredCombinations (error) {
      const gap = expansionCoverageGapFromError(error)
      if (!gap) throw error

      this.uncoveredCombinations = gap
      this.openOrUpdateDialog({ id: this.uncoveredCombinationsDialogId, width: '900px' })
    },

    runtimeCounterPartyIsValidForByproductOrLogYardSale (runtimeCounterparty) {
      return runtimeCounterparty === RuntimeCounterparty.Driver.value ||
      runtimeCounterparty === RuntimeCounterparty.DestinationAccount.value ||
      runtimeCounterparty === RuntimeCounterparty.ContractAccount.value
    },

    sanitizeRTCP (runtimeCounterparty) {
      return this.runtimeCounterPartyIsValidForByproductOrLogYardSale(runtimeCounterparty) ? runtimeCounterparty : undefined
    },

    baseRateEnteredForActivity ({ amount, itemId }) {
      const ab = this.activityBuilders.find(a => a.activityBuilderId === itemId)

      if (ab) {
        ab.baseRate = amount
      }
    },

    areBuildersValid () {
      const missingRateSheet = this.activityBuilders.find(b => this.isPayByVolume(b) && !b.rateSheet)
      if (missingRateSheet) {
        this.setSnackError(`${missingRateSheet.activityTemplateName}: ${this.$t('rateSheetMissingOnPayByVolume')}`)
        return false
      }

      let isValid = true
      let accountsForSameActivity = []
      let accountDupsHash = {}
      let dupAccountId
      let dupActivityName
      /*
        Checks to see if an activity has duplicate counterparties. Gets a set of all builder names.
        Filter list of builders by name and maps to account id. Then hashes the account ids to find dups.
        If invalid sets gives an error message with account name and activity name.
      */
      const builderNames = new Set(this.activityBuilders.map(builder => builder.activityTemplateName))
      builderNames.forEach(name => {
        accountsForSameActivity = this.activityBuilders.filter(nestedBuilder => nestedBuilder.activityTemplateName === name && !nestedBuilder.runtimeField)
        accountsForSameActivity = accountsForSameActivity.map(mapBuilder => mapBuilder.counterparty ? mapBuilder.counterparty.accountId : mapBuilder.counterpartyId)
        accountsForSameActivity.forEach(accountId => {
          if (accountDupsHash[accountId]) {
            dupActivityName = name
            dupAccountId = accountId
            isValid = false
          } else accountDupsHash[accountId] = true
        })
        const duplicates = Object.keys(this.getDuplicateCounterparties()).map(duplicate => Number(duplicate.split('-')[1]))
        duplicates.forEach(accountId => {
          dupActivityName = name
          dupAccountId = accountId
          isValid = false
        })
        accountsForSameActivity = []
        accountDupsHash = {}
      })
      if (!isValid) {
        const duplicateAccount = this.allAccounts.find(account => account.accountId === dupAccountId)
        this.setSnackError(`${dupActivityName} already has an activity using ${duplicateAccount?.name} counterparty.`)
      }
      return isValid
    },

    addProductClicked () {
      this.openOrUpdateDialog(this.dialogId)
    },

    productsSelected (e) {
      this.productSelectorState = e
    },

    addProductSubmitted () {
      this.products = [...new Set([...this.products, ...this.productSelectorState])]
      this.activityBuilders
        .filter(ab => this.allowBaseRate(ab)) // TODO MK: Reevaluate if non-inventory can't have details
        .forEach(ab => {
          this.productSelectorState.forEach(p => {
            if (!ab.limitToBaseRate) this.addProductDetail(ab, p)
          })
        })
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    expectedValue (activityTemplateId, productId) {
      const activityStats = this.templateStats?.get(activityTemplateId)
      if (!productId) return activityStats?.baseRecentAverage
      return activityStats?.detailStats.find(p => p.productId === productId)?.recentAverage
    },

    missingProducts (builder, products) {
      return products.filter(p => !(builder?.products ?? []).some(bp => bp.productId === p.productId))
    },

    addProductDetail (builder, product) {
      const newDetail = {
        productId: product.productId,
        name: product.name,
        baseRate: builder.baseRate
      }

      builder.products.push(newDetail)
    },

    removeProductDetail (builder, detail) {
      const ab = this.activityBuilders.find(ab => ab.activityBuilderId === builder.activityBuilderId)
      if (!ab) return
      const productIndex = ab.products.findIndex(pd => pd.productId === detail.productId)
      if (productIndex === -1) return
      ab.products.splice(productIndex, 1)
    },

    baseRateEnteredForProduct ({ amount, itemId }) {
      let [activityBuilderId, productId] = itemId.split('-')
      activityBuilderId = parseInt(activityBuilderId)
      productId = parseInt(productId)

      const activityIndex = this.activityBuilders.findIndex(a => a.activityBuilderId === activityBuilderId)

      if (activityIndex !== -1) {
        const productIndex = this.activityBuilders[activityIndex].products.findIndex(d => d.productId === productId)

        if (productIndex !== -1) {
          this.activityBuilders[activityIndex].products[productIndex].baseRate = amount
        }
      }
    },

    allowBaseRate (item) {
      return item.specialization !== TemplateSpecialization.Penalty.value || item.category === AccountingCategory.Receivable.value
    },

    builderIsDynamicCounterparty (builder) {
      return (builder.runtimeField !== undefined && builder.runtimeField !== null)
    },

    counterpartyTextFromRuntimeField (runtimeField) {
      if (runtimeField === RuntimeCounterparty.Driver.value) return this.$t('ticketDriver')
      const ids = this.runtimeFieldCounterpartyIdMap.get(runtimeField)
      switch (ids?.length) {
        case 0:
          return this.$t('notSet')
        case 1:
          return this.findAccount(ids[0])?.name ?? this.$t('notSet')
        default:
          return this.$t('multiple')
      }
    },

    initializeCounterpartyRuntimeFieldMap () {
      this.runtimeFieldCounterpartyIdMap = new Map()
      const valueOrEmptyArray = (value) => { return !this.nullOrUndefined(value) ? [value] : [] }
      this.runtimeFieldCounterpartyIdMap.set(RuntimeCounterparty.ContractAccount.value, valueOrEmptyArray(this.contract.accountId))
      this.runtimeFieldCounterpartyIdMap.set(RuntimeCounterparty.DestinationAccount.value, valueOrEmptyArray(this.contract.destinationAccountId))
    },

    findAccount (accountId) {
      return this.allAccounts[this.accountIndices[accountId]]
    },

    counterpartyIsDuplicate (activityTemplateId, counterpartyId) {
      if (this.nullOrUndefined(counterpartyId)) return false
      return this.duplicateCounterparties[`${activityTemplateId}-${counterpartyId}`] ?? false
    },

    runtimeFieldResolvesToDuplicateCounterparty (activityTemplateId, runtimeField) {
      if (runtimeField === RuntimeCounterparty.Driver.value) return false
      const ids = this.runtimeFieldCounterpartyIdMap.get(runtimeField) ?? []
      return ids.some(id => this.duplicateCounterparties[`${activityTemplateId}-${id}`] ?? false)
    },

    rcpCanResolve (rcp) {
      if (rcp === RuntimeCounterparty.Driver.value) return true
      const ids = this.runtimeFieldCounterpartyIdMap.get(rcp)
      return ids?.length > 0
    },

    getDuplicateCounterparties () {
      const counts = {}
      const duplicates = {}
      for (const builder of this.activityBuilders) {
        const activityTemplateId = builder?.activityTemplateId
        let targetCounterpartiesIds = !this.nullOrUndefined(builder.runtimeField) ? this.runtimeFieldCounterpartyIdMap.get(builder.runtimeField) : [builder?.counterparty?.accountId]
        targetCounterpartiesIds ??= []
        targetCounterpartiesIds.forEach(tcp => {
          if (tcp !== undefined) {
            const key = `${activityTemplateId}-${tcp}`
            counts[key] = (counts[key] !== undefined) ? counts[key] + 1 : 1
          }
        })
      }
      Object.keys(counts).forEach(key => {
        if (counts[key] > 1) {
          duplicates[key] = true
        }
      })
      return duplicates
    },

    showMissingMilesIcon (item) {
      return item.modifier === ActivityModifier.DistanceTraveled.value &&
             (this.contract?.distance === 0 || this.contract?.distance === null)
    },

    isPayByVolume (builder) {
      return builder.payBy === PayBy.Volume.value
    },

    getRateSheetName (builder) {
      const r = builder.rateSheet
      if (typeof r === 'string') return r
      if (typeof r?.name === 'string') return r.name
      return r?.rateSheetDefinition?.name || null
    },

    async rateSheetDetailClicked (builder) {
      this.focusedBuilder = builder
      if (!this.accountRateSheetDefinitionsFetched && this.contract?.destinationAccountId) {
        this.accountRateSheetDefinitionsFetched = true
        await this.fetchAccountRateSheetDefinitions(this.contract.destinationAccountId)
      }
      this.openOrUpdateDialog({ id: this.rateSheetDialogId, width: 1000 })
    },

    rateSheetConfirmed (rateSheet) {
      if (this.focusedBuilder) {
        this.$set(this.focusedBuilder, 'rateSheet', rateSheet)
      }
      this.closeRateSheetDialog()
    },

    closeRateSheetDialog () {
      this.focusedBuilder = null
      this.editingRateSheet = false
      this.closeDialogsAtOrAbove(this.rateSheetDialogId)
    }
  }
}
</script>

<style>
#add-with-bundle-table>.v-data-table__wrapper tbody tr.v-data-table__expanded__content {
  background-color:#eee;
  box-shadow: inset 0 0 0 0 #00000000;
  transition: .05s;
}

#add-with-bundle-table>.v-data-table__wrapper tbody tr.v-data-table__expanded__content:hover {
  background-color:#e0e0e0;
  box-shadow: inset 0 0 0 0 #00000000;
  transition: .05s;
}
</style>
